import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'

import { AuthChrome, PasswordField } from '../../components/AuthChrome'
import { Button, Field, Notice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'

type Step = 'request' | 'confirm'

export function ForgotPassword() {
  const { requestPasswordReset, confirmPasswordReset, authError } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const handleRequest = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim()) {
      setLocalError('Enter the email on your account.')
      return
    }
    setLocalError(null)
    setIsSubmitting(true)
    try {
      await requestPasswordReset(email)
      // Move to the code step whatever Cognito says. It answers identically for
      // an address it does not know, and revealing the difference here would
      // turn this form into an account-existence oracle.
      setStep('confirm')
    } catch {
      setStep('confirm')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirm = async (e: FormEvent) => {
    e.preventDefault()
    if (!code.trim() || !password) {
      setLocalError('Enter the code and a new password.')
      return
    }
    setLocalError(null)
    setIsSubmitting(true)
    try {
      await confirmPasswordReset(email, code, password)
      navigate('/sign-in', { replace: true })
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'That code was not accepted.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthChrome
      eyebrow="Account recovery"
      title={step === 'request' ? 'Reset your password' : 'Enter your code'}
      lead={
        step === 'request'
          ? 'We will send a code to the email on your account.'
          : 'If that address has an account, a six-digit code is on its way to it.'
      }
      footer={<Link className="auth__link" to="/sign-in">Back to sign in</Link>}
    >
      {step === 'request' ? (
        <form className="auth__form" onSubmit={handleRequest} noValidate>
          {(localError || authError) && <Notice tone="error">{localError ?? authError}</Notice>}
          <Field
            label="Email"
            type="email"
            value={email}
            autoComplete="username"
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" full busy={isSubmitting}>
            {isSubmitting ? 'Sending' : 'Send reset code'}
          </Button>
        </form>
      ) : (
        <form className="auth__form" onSubmit={handleConfirm} noValidate>
          {localError && <Notice tone="error">{localError}</Notice>}
          <Field
            label="Verification code"
            code
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            onChange={(e) => setCode(e.target.value)}
          />
          <PasswordField
            label="New password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            hint="At least 8 characters."
          />
          <Button type="submit" full busy={isSubmitting}>
            {isSubmitting ? 'Saving' : 'Set new password'}
          </Button>
          <button type="button" className="auth__link auth__link--block" onClick={() => setStep('request')}>
            Use a different email
          </button>
        </form>
      )}
    </AuthChrome>
  )
}
